import { useQuery } from '@tanstack/react-query';

type PlatformMode = 'full' | 'api_as_service';

interface PlatformModeResponse {
  mode: PlatformMode;
  timestamp: string;
  raw_env_var: string;
}

const platformModeQueryKey = ['platform-mode'];

async function fetchPlatformMode(): Promise<PlatformMode> {
  const backendUrl =
    (window as any).RUNTIME_ENV?.BACKEND_URL ??
    process.env.BACKEND_URL ??
    'http://localhost:8000/api/v1';

  const response = await fetch(`${backendUrl}/platform-mode`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data: PlatformModeResponse = await response.json();
  return data.mode;
}

// Deployment config: it cannot change while the tab is open, hence staleTime
// Infinity. One request per page load, shared by every caller.
export const usePlatformMode = (): { mode: PlatformMode; loading: boolean; error: string | null } => {
  const { data, isLoading, error } = useQuery({
    queryKey: platformModeQueryKey,
    queryFn: fetchPlatformMode,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  return {
    // Fall back to full mode: a failed lookup must not hide the app.
    mode: data ?? 'full',
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : 'Unknown error') : null,
  };
};
